import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UnderlagSections } from "@/components/UnderlagSections";
import { Loader2, Download, FileText, ShieldCheck, Share2, Copy, AlertCircle } from "lucide-react";

export default function Underlag() {
  const [data, setData] = useState(null);
  const [reviewed, setReviewed] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [sharing, setSharing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/underlag").then((r) => { setData(r.data); setReviewed(r.data.reviewed || []); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const toggleReview = async (sectionId, val) => {
    setReviewed((prev) => (val ? [...new Set([...prev, sectionId])] : prev.filter((x) => x !== sectionId)));
    try {
      const { data: res } = await api.put("/underlag/review", { section_id: sectionId, reviewed: val });
      setReviewed(res.reviewed || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/underlag/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "HAMN_Mitt_underlag.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Underlaget laddades ner");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDownloading(false); }
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const { data: res } = await api.post("/underlag/share-link");
      setShareLink(res.link);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSharing(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(shareLink); toast.success("Länk kopierad"); };

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  const missing = data.sections.filter((s) => !s.filled);

  return (
    <div data-testid="underlag-page">
      <PageHeader
        title="Mitt underlag"
        description="Här ser du allt ditt råmaterial samlat på ett ställe – profil, karaktär, den guidade intervjun och dina referenssvar. Granska att allt stämmer innan du bygger ett CV."
      />

      <Card className="rounded-2xl border-border p-6 mb-6" data-testid="underlag-completeness">
        <div className="flex items-center justify-between gap-3 mb-2">
          <h2 className="font-head font-semibold text-lg">Så komplett är ditt underlag</h2>
          <span className="font-head font-bold text-2xl text-primary" data-testid="underlag-percent">{data.completeness}%</span>
        </div>
        <Progress value={data.completeness} className="h-2.5" />
        <p className="text-sm text-muted-foreground mt-2">{data.filled_count} av {data.total_sections} avsnitt ifyllda.</p>
        {missing.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
              <AlertCircle className="h-4 w-4 text-accent" /> Det här saknas ännu:
            </div>
            <div className="flex flex-wrap gap-2" data-testid="underlag-missing">
              {missing.map((s) => (
                <button
                  key={s.id}
                  onClick={() => s.edit && navigate(s.edit)}
                  className="rounded-full border border-border bg-muted/50 px-3 py-1 text-sm hover:border-primary hover:text-primary transition-colors"
                  data-testid={`underlag-missing-${s.id}`}
                >
                  {s.title} →
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button className="rounded-full" onClick={downloadPdf} disabled={downloading} data-testid="underlag-download-pdf">
          <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Skapar PDF…" : "Ladda ner underlag (PDF)"}
        </Button>
        <Dialog onOpenChange={(o) => { if (o && !shareLink) createShareLink(); }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-full" data-testid="underlag-share-btn">
              <Share2 className="h-4 w-4 mr-1.5" /> Dela med handledare
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dela ditt underlag</DialogTitle>
              <DialogDescription>
                Länken låter en handledare läsa ditt underlag utan att logga in. Den gäller i 7 dagar. Dela bara med någon du litar på.
              </DialogDescription>
            </DialogHeader>
            {sharing ? (
              <div className="flex justify-center py-6"><Loader2 className="animate-spin text-primary" /></div>
            ) : (
              <div className="flex items-center gap-2">
                <Input readOnly value={shareLink} data-testid="underlag-share-link" className="font-mono text-xs" />
                <Button className="rounded-full shrink-0" onClick={copyLink} data-testid="underlag-copy-link"><Copy className="h-4 w-4" /></Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Button variant="outline" className="rounded-full" onClick={() => navigate("/app/skapa-cv")} data-testid="underlag-create-cv">
          <FileText className="h-4 w-4 mr-1.5" /> Allt stämmer – skapa CV
        </Button>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6">
        <ShieldCheck className="h-4 w-4 text-primary" /> AI använder bara det du själv lagt in – inget hittas på. Referenser och karaktärsprofil hålls separerade.
      </div>

      <UnderlagSections sections={data.sections} reviewed={reviewed} onToggleReview={toggleReview} onEdit={(to) => navigate(to)} />
    </div>
  );
}
